<template>
  <div>
    <page-header
      :title="title"
      :container-class="containerClass"
      info-text="Manage skills library with video tutorials and demonstrations."
    />
    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-start align-items-lg-center skill-library-toolbar">
              <!-- Search -->
              <div class="col-12 col-lg-auto mb-2 mb-lg-0">
                <form class="search-form search-form--light d-flex skill-library-search" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search library" v-model="searchTerm" @input="onSearch" />

                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <!-- Filters and Add Btn -->
              <div class="col-12 col-lg d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center justify-content-lg-end">
                <div class="skill-library-filters d-flex flex-column flex-sm-row flex-wrap align-items-stretch align-items-sm-center">
                  <b-form-select
                    class="skill-library-filter mr-0 mr-sm-2 mb-2 mb-sm-0"
                    size="sm"
                    v-model="categoryFilter"
                    :options="categoryOptions"
                  />
                  <b-form-select
                    class="skill-library-filter mr-0 mr-sm-2 mb-2 mb-sm-0"
                    size="sm"
                    v-model="sourceFilter"
                    :options="sourceOptions"
                  />
                  <b-btn size="sm" variant="outline-secondary" @click="clearFilters" class="skill-library-clear-btn">Clear</b-btn>
                </div>

                <b-btn class="skill-library-add-btn ml-0 ml-lg-2 mt-2 mt-lg-0" :to="{ name: 'sa-new-skill-library' }" exact variant="primary"
                  >Add to Library</b-btn
                >
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="libraryItems"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap sticky-lcol-table skill-library-table"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
            @row-clicked="editLibraryItem"
            show-empty
          >
            <template #table-busy>
              <div class="text-center">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <template #empty>
              <div class="text-center">No items in library.</div>
            </template>

            <template #cell(thumbnail)="data">
              <img
                :src="data.item.thumbnail_url"
                class="rounded skill-library-thumb"
                v-if="data.item.thumbnail_url"
              />
              <video
                :src="data.item.video_url"
                class="rounded skill-library-thumb"
                v-else
              ></video>
            </template>

            <template #cell(program)="data">
              <span v-if="data.item.program">{{ getProgramName(data.item.program) }}</span>
              <span v-else class="text-muted">-</span>
            </template>

            <template #cell(assigned_school)="data">
              <div v-if="data.item.assigned_school && data.item.assigned_school.length > 0">
                <b-badge
                  v-for="schoolId in data.item.assigned_school.slice(0, 2)"
                  :key="schoolId"
                  variant="secondary"
                  class="mr-1 mb-1"
                  style="font-weight: 500;"
                >
                  {{ getSchoolName(schoolId) }}
                </b-badge>
                <b-badge
                  v-if="data.item.assigned_school.length > 2"
                  variant="light"
                  class="mr-1 mb-1"
                  style="font-weight: 500;"
                >
                  +{{ data.item.assigned_school.length - 2 }} more
                </b-badge>
              </div>
              <span v-else class="text-muted">All Schools</span>
            </template>

            <template #cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret @click.stop>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item @click.stop :to="{ name: 'sa-edit-skill-library', params: { id: data.item.id } }">
                  <md-icon class="text-info mr-2">edit</md-icon>
                  {{ $t('generalMsgs.edit') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click.stop="removeLibraryItem(data.item.id)">
                  <md-icon class="text-danger mr-2">delete</md-icon>
                  {{ $t('generalMsgs.remove') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination v-model="currentPage" :total-rows="totalItems" :per-page="perPage" @change="onPageChange" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DEFAULT_PAGE_SIZE } from '@/common/constants';
import Page from '@/components/Page.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import { debounce } from 'lodash';
import { mapActions } from 'vuex';
import moment from 'moment';

export default {
  components: { MdIcon, PageHeader, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Skills Library',

      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalItems: 0,
      searchTerm: '',
      libraryItems: [],
      ordering: '',
      allPrograms: [],
      allSchools: [],
      categoryFilter: '',
      sourceFilter: '',
      categoryOptions: [
        { value: '', text: 'All Categories' },
        { value: 'hygiene', text: 'Hygiene' },
        { value: 'mobility', text: 'Mobility' },
        { value: 'vital_signs', text: 'Vital Signs' },
        { value: 'personal_care', text: 'Personal Care' },
        { value: 'elimination', text: 'Elimination' },
        { value: 'positioning', text: 'Positioning' },
        { value: 'nutrition', text: 'Nutrition' },
        { value: 'infection_control', text: 'Infection Control' },
        { value: 'range_of_motion', text: 'Range of Motion' },
        { value: 'bed_making', text: 'Bed Making' },
        { value: 'safety', text: 'Safety' },
        { value: 'emergency', text: 'Emergency' },
      ],
      sourceOptions: [
        { value: '', text: 'All Sources' },
        { value: 'Transition', text: 'Transition' },
        { value: 'Skillform', text: 'Skillform' },
        { value: 'Hartman', text: 'Hartman' },
        { value: '4YourCNA', text: '4YourCNA' },
        { value: 'NURSE JAR', text: 'NURSE JAR' },
        { value: 'Nurse Eunice', text: 'Nurse Eunice' },
        { value: 'CNASkill', text: 'CNASkill' },
        { value: 'XY Nursing Skills', text: 'XY Nursing Skills' },
        { value: 'All Healthcare Careers', text: 'All Healthcare Careers' },
      ],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Skills Library', active: true },
      ];
    },

    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          class: 'text-left',
          thStyle: { minWidth: '64px' },
        },
        { key: 'thumbnail', label: '', tdClass: 'clickable-item', thStyle: { width: '60px' } },
        { key: 'title', label: 'Title', tdClass: 'clickable-item', sortable: true },
        { key: 'description', label: 'Description', tdClass: 'clickable-item' },
        { key: 'program', label: 'Program', tdClass: 'clickable-item' },
        // { key: 'assigned_school', label: 'Assigned Schools', tdClass: 'clickable-item' },
        { key: 'created_at', label: 'Created', tdClass: 'clickable-item', sortable: true },
      ];
    },
  },

  methods: {
    ...mapActions('skills', ['getSkillsLibrary', 'deleteSkillLibrary']),
    ...mapActions('program', ['getAllPrograms']),

    async fetchLibraryItems(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      try {
        const response = await this.getSkillsLibrary({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.categoryFilter && { category: this.categoryFilter }),
          ...(this.sourceFilter && { source: this.sourceFilter }),
          ...params,
        });

        // Handle the response structure: { status_code, data: { count, results: [...] }, message }
        if (response && response.data && Array.isArray(response.data.results)) {
          this.libraryItems = response.data.results;
          this.totalItems = response.data.count || this.libraryItems.length;
        } else if (response && Array.isArray(response.data)) {
          this.libraryItems = response.data;
          this.totalItems = this.libraryItems.length;
        } else if (Array.isArray(response)) {
          this.libraryItems = response;
          this.totalItems = this.libraryItems.length;
        } else {
          console.error('Unexpected response structure');
          this.libraryItems = [];
          this.totalItems = 0;
        }

        this.currentPage = pageNum;
      } catch (error) {
        this.libraryItems = [];
        this.totalItems = 0;
        this.makeToast({ variant: 'danger', msg: 'Failed to load library items' });
      }

      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchLibraryItems(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchLibraryItems();
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    clearFilters() {
      this.categoryFilter = '';
      this.sourceFilter = '';
      this.fetchLibraryItems();
    },

    debouncedSearch: debounce(vm => {
      vm.fetchLibraryItems();
    }, 500),

    editLibraryItem(data) {
      this.$router.push({ name: 'sa-edit-skill-library', params: { id: data.id } });
    },

    async removeLibraryItem(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to remove this item from the library. It will no longer be available.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        );
        if (isConfirmed) {
          this.isLoading = true;
          await this.deleteSkillLibrary(id);
          this.fetchLibraryItems();
          this.makeToast({ variant: 'success', msg: 'Library item removed successfully!' });
          this.isLoading = false;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.isLoading = false;
      }
    },

    async fetchPrograms() {
      try {
        const response = await this.getAllPrograms({ ordering: 'id' });
        this.allPrograms = response.data || [];
      } catch (error) {
        console.error('Error fetching programs:', error);
        this.allPrograms = [];
      }
    },

    getProgramName(programId) {
      const program = this.allPrograms.find(p => p.id === programId);
      return program ? program.title : '-';
    },

    getSchoolName(schoolId) {
      const school = this.allSchools.find(s => s.id === schoolId);
      return school ? school.name : `School ${schoolId}`;
    },

    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },

  async mounted() {
    try {
      await this.fetchPrograms();
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    await this.fetchLibraryItems();
  },
};
</script>

<style scoped lang="scss">
.skill-library-toolbar {
  row-gap: 12px;
}

.skill-library-search {
  width: 100%;
  max-width: 360px;
}

.skill-library-filters {
  width: 100%;
  gap: 8px;
}

.skill-library-filter {
  min-width: 180px;
  flex: 1;
}

.skill-library-clear-btn {
  min-width: 88px;
  align-self: stretch;
}

.skill-library-add-btn {
  white-space: nowrap;
  min-width: 140px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.skill-library-thumb {
  width: 44px;
  height: 44px;
  object-fit: cover;
}

::v-deep .skill-library-table thead th {
  font-size: 12px;
  letter-spacing: 0.08em;
  color: #5f6b7a;
  vertical-align: middle;
}

::v-deep .skill-library-table tbody td {
  vertical-align: middle;
}

::v-deep .skill-library-table tbody tr:hover td {
  background: #f8fbff;
}

@media (max-width: 991px) {
  .skill-library-search {
    max-width: 100%;
  }

  .skill-library-add-btn {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .skill-library-filter,
  .skill-library-clear-btn {
    width: 100%;
    min-width: 100%;
  }
}
</style>
